import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../firebase";

interface WishlistState {
  vacant: { listingId: string }[];
  sale: { listingId: string }[];
  shortstays: { listingId: string }[];
  cleaning: { listingId: string }[];
  loading: boolean;
  error: string | null;
}

const initialState: WishlistState = {
  vacant: [],
  sale: [],
  shortstays: [],
  cleaning: [],
  loading: false,
  error: null,
};

// 🔄 Helper: Get Firestore collection name
const getCollection = (type: "vacant" | "sale" | "shortstays" | "cleaning"): string => {
  switch (type) {
    case "vacant":
      return "vacantwishlist";
    case "sale":
      return "salewishlist";
    case "shortstays":
      return "shortstaywishlist";
    case "cleaning":
      return "cleaningwishlist";
    default:
      throw new Error("Invalid wishlist type");
  }
};

// ✅ Add listing to wishlist
export const addToWishlist = createAsyncThunk(
  "wishlist/addToWishlist",
  async (
    { listingId, userId, type }: { listingId: string; userId: string; type: "vacant" | "sale" | "shortstays" | "cleaning" },
    { rejectWithValue }
  ) => {
    try {
      const ref = doc(db, getCollection(type), userId);
      await setDoc(ref, { listingIds: arrayUnion(listingId) }, { merge: true });
      return { listingId, type };
    } catch (error: any) {
      console.error("Error adding to wishlist:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Remove listing from wishlist
export const removeFromWishlist = createAsyncThunk(
  "wishlist/removeFromWishlist",
  async (
    { listingId, userId, type }: { listingId: string; userId: string; type: "vacant" | "sale" | "shortstays" | "cleaning" },
    { rejectWithValue }
  ) => {
    try {
      const ref = doc(db, getCollection(type), userId);
      await updateDoc(ref, { listingIds: arrayRemove(listingId) });
      return { listingId, type };
    } catch (error: any) {
      console.error("Error removing from wishlist:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Fetch wishlist
export const fetchWishlist = createAsyncThunk(
  "wishlist/fetchWishlist",
  async (
    { userId, type }: { userId: string; type: "vacant" | "sale" | "shortstays" | "cleaning" },
    { rejectWithValue }
  ) => {
    try {
      const ref = doc(db, getCollection(type), userId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return { type, listingIds: [] };
      return { type, listingIds: snap.data().listingIds || [] };
    } catch (error: any) {
      console.error("Error fetching wishlist:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addToWishlist.fulfilled, (state, action: PayloadAction<{ listingId: string; type: "vacant" | "sale" | "shortstays" | "cleaning" }>) => {
        const { type, listingId } = action.payload;
        state[type].push({ listingId });
      })
      .addCase(removeFromWishlist.fulfilled, (state, action: PayloadAction<{ listingId: string; type: "vacant" | "sale" | "shortstays" | "cleaning" }>) => {
        const { type, listingId } = action.payload;
        state[type] = state[type].filter((item) => item.listingId !== listingId);
      })
      .addCase(fetchWishlist.fulfilled, (state, action: PayloadAction<{ type: "vacant" | "sale" | "shortstays" | "cleaning"; listingIds: string[] }>) => {
        const { type, listingIds } = action.payload;
        state[type] = listingIds.map((id) => ({ listingId: id }));
      })
      .addMatcher(
        (action) => action.type.startsWith("wishlist") && action.type.endsWith("rejected"),
        (state, action: PayloadAction<any>) => {
          state.error = action.payload;
        }
      );
  },
});

export default wishlistSlice.reducer;